// Admin/manager page for supplier CRUD.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, X, Check } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface Supplier {
  id: string;
  name: string;
  phone: string;
  address: string;
  note: string;
  isActive: boolean;
}

interface EditState {
  name: string;
  phone: string;
  address: string;
  note: string;
}

const emptyEdit: EditState = { name: "", phone: "", address: "", note: "" };

export function SuppliersPage() {
  const qc = useQueryClient();
  const [editId, setEditId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>(emptyEdit);
  const [showNew, setShowNew] = useState(false);
  const [newState, setNewState] = useState<EditState>(emptyEdit);

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () =>
      apiClient.get<{ data: Supplier[] }>("/v1/suppliers").then((r) => r.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) =>
      apiClient.patch(`/v1/suppliers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setEditId(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: object) => apiClient.post("/v1/suppliers", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setShowNew(false);
      setNewState(emptyEdit);
    },
  });

  function startEdit(s: Supplier) {
    setEditId(s.id);
    setEditState({ name: s.name, phone: s.phone, address: s.address, note: s.note });
  }

  function saveEdit(id: string) {
    updateMutation.mutate({ id, body: editState });
  }

  function toggleActive(s: Supplier) {
    updateMutation.mutate({ id: s.id, body: { isActive: !s.isActive } });
  }

  function field(key: keyof EditState, state: EditState, setter: (s: EditState) => void, placeholder: string) {
    return (
      <Input
        className="h-7 text-sm"
        placeholder={placeholder}
        value={state[key]}
        onChange={(e) => setter({ ...state, [key]: e.target.value })}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Nhà cung cấp</h1>
        <Button size="sm" className="gap-1.5" onClick={() => setShowNew((v) => !v)}>
          <Plus size={16} /> Thêm NCC
        </Button>
      </div>

      {showNew && (
        <div className="flex flex-wrap gap-2 rounded-md border p-3 bg-muted/20">
          {field("name", newState, setNewState, "Tên NCC *")}
          {field("phone", newState, setNewState, "Điện thoại")}
          {field("address", newState, setNewState, "Địa chỉ")}
          {field("note", newState, setNewState, "Ghi chú")}
          <Button
            size="sm"
            disabled={!newState.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(newState)}
          >
            Lưu
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowNew(false)}>
            <X size={16} />
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Tên</Th>
              <Th>Điện thoại</Th>
              <Th>Địa chỉ</Th>
              <Th>Ghi chú</Th>
              <Th>Trạng thái</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {suppliers.length === 0 && (
              <Tr>
                <Td colSpan={6} className="text-center text-muted-foreground">
                  Chưa có nhà cung cấp
                </Td>
              </Tr>
            )}
            {suppliers.map((s) => (
              <Tr key={s.id}>
                {editId === s.id ? (
                  <>
                    <Td>{field("name", editState, setEditState, "Tên *")}</Td>
                    <Td>{field("phone", editState, setEditState, "Điện thoại")}</Td>
                    <Td>{field("address", editState, setEditState, "Địa chỉ")}</Td>
                    <Td>{field("note", editState, setEditState, "Ghi chú")}</Td>
                    <Td>—</Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveEdit(s.id)}>
                          <Check size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditId(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    </Td>
                  </>
                ) : (
                  <>
                    <Td className="font-medium">{s.name}</Td>
                    <Td>{s.phone || "—"}</Td>
                    <Td>{s.address || "—"}</Td>
                    <Td className="text-muted-foreground text-xs">{s.note || "—"}</Td>
                    <Td>
                      <button
                        onClick={() => toggleActive(s)}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer ${
                          s.isActive ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {s.isActive ? "Hoạt động" : "Ẩn"}
                      </button>
                    </Td>
                    <Td>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(s)}>
                        <Pencil size={14} />
                      </Button>
                    </Td>
                  </>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
